import { TAGS } from "../constants";
import {
  CreateCreditNoteArgs,
  Dispute,
  Invoice,
  ManualChargeInvoiceArgs,
  OneTimePaymentSuccessArgs,
  PatientProfile,
  PaymentIntent,
  PayStripeInvoice,
  StripeInvoice,
} from "../types";
import { api } from "./api";

export const invoicesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPatientInvoices: builder.query<
      Invoice[],
      {
        patientId?: number;
        status?: string;
      }
    >({
      query: (params) => ({
        method: "GET",
        url: `/api/invoices`,
        params: {
          patient_id: params.patientId ?? undefined,
          status: params.status ?? undefined,
        },
      }),
      providesTags: [TAGS.INVOICES],
    }),
    chargePatientNow: builder.mutation<
      StripeInvoice,
      { invoiceId: string; paymentMethodId?: string }
    >({
      query: ({ invoiceId, ...body }) => ({
        method: "POST",
        url: `/api/invoices/${invoiceId}/pay`,
        body,
      }),
      invalidatesTags: [TAGS.INVOICES],
    }),
    addCreditNoteToInvoice: builder.mutation<void, CreateCreditNoteArgs>({
      query: ({ invoice, ...body }) => ({
        method: "POST",
        url: `/api/invoices/${invoice}/add-credit-note`,
        body,
      }),
      invalidatesTags: [TAGS.INVOICES],
    }),

    voidInvoice: builder.mutation<void, string>({
      query: (invoiceId) => ({
        url: `/api/invoices/${invoiceId}/void`,
        method: "POST",
      }),
      invalidatesTags: [TAGS.INVOICES, TAGS.PATIENT],
    }),
    processOneTimeInvoice: builder.mutation<
      {
        paymentIntent: PaymentIntent;
        invoice: PayStripeInvoice;
      },
      {
        patient: Pick<PatientProfile, "id" | "stripe_customer_id">;
        body: {
          priceId: string;
          paymentMethodId?: string;
          couponId?: number;
          quantity?: number;
        };
      }
    >({
      query: (body) => ({
        method: "POST",
        url: `/api/invoices/one-time`,
        body,
      }),
    }),
    afterOneTimePaymentSuccess: builder.mutation<
      void,
      OneTimePaymentSuccessArgs
    >({
      query: (body) => ({
        method: "POST",
        url: `/api/invoices/one-time-payment-success`,
        body,
      }),
      invalidatesTags: [TAGS.INVOICES, TAGS.TASKS],
    }),
    getInvoiceById: builder.query<Invoice, string>({
      query: (invoiceId) => ({
        method: "GET",
        url: `/api/invoices/${invoiceId}`,
      }),
      providesTags: [TAGS.INVOICES, TAGS.ORDERS],
    }),
    getPatientDisputes: builder.query<Dispute[], string>({
      query: (customerId) => ({
        method: "GET",
        url: `/api/invoices/${customerId}/disputes`,
      }),
      providesTags: (_result, _error, args) => [
        {
          type: TAGS.DISPUTES,
          id: args,
        },
      ],
    }),
    createManualCharge: builder.mutation<Invoice, ManualChargeInvoiceArgs>({
      query: (body) => ({
        method: "POST",
        url: `/api/invoices/manual-charge`,
        body,
      }),
      invalidatesTags: [TAGS.INVOICES],
    }),
  }),
});

export const {
  useGetPatientInvoicesQuery,
  useChargePatientNowMutation,
  useAddCreditNoteToInvoiceMutation,
  useProcessOneTimeInvoiceMutation,
  useAfterOneTimePaymentSuccessMutation,
  useVoidInvoiceMutation,
  useGetInvoiceByIdQuery,
  useGetPatientDisputesQuery,
  useCreateManualChargeMutation,
} = invoicesApi;
